import {
  MOSIDA_API_BASE_URL,
  buildImageReadApiUrl as buildMosidaImageReadApiUrl,
} from '../shop/mosidaApi';

export const FAVOURITES_PAGE_SIZE = 20;

export function buildImageReadApiUrl(sourceUrl) {
  return buildMosidaImageReadApiUrl(sourceUrl);
}

function extractList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return (
    payload?.data?.favourites ||
    payload?.data?.items ||
    payload?.favourites ||
    payload?.items ||
    []
  );
}

function resolveHasMore(payload, page, pageSize, listLength) {
  const meta =
    payload?.meta ||
    payload?.pagination ||
    payload?.data?.meta ||
    payload?.data?.pagination ||
    {};

  if (typeof payload?.hasMore === 'boolean') return payload.hasMore;
  if (typeof payload?.has_more === 'boolean') return payload.has_more;
  if (typeof meta.hasMore === 'boolean') return meta.hasMore;
  if (typeof meta.has_more === 'boolean') return meta.has_more;

  const totalPages = Number(
    meta.totalPages || meta.total_pages || payload?.totalPages || payload?.total_pages
  );
  if (Number.isFinite(totalPages) && totalPages > 0) {
    return page < totalPages;
  }

  const total = Number(meta.total || payload?.total || payload?.data?.total);
  if (Number.isFinite(total) && total >= 0) {
    return page * pageSize < total;
  }

  return listLength >= pageSize;
}

export function normalizeFavourite(item) {
  if (!item || typeof item !== 'object') return null;
  const imageUrl = String(
    item.imageUrl ||
      item.image_url ||
      item.url ||
      item.image ||
      item.imgURL ||
      item.screenshot ||
      item.fileUrl ||
      ''
  ).trim();
  if (!imageUrl) return null;
  return {
    id: String(item.id || item.favouriteId || imageUrl),
    imageUrl,
    type: String(item.type || item.name || item.station || item.category || ''),
    userId: item.userId || item.user_id || null,
    ticketId: item.ticketId || item.ticket_id || item.ticket?.id || null,
    createdAt: item.createdAt || item.created_at || '',
    updatedAt: item.updatedAt || item.updated_at || '',
    ticket: item.ticket || null,
  };
}

/**
 * GET /favourites?userId=&page=&items=
 * All of the user's favourites across visits (not scoped to a ticket).
 */
export async function getFavouritesApi({
  userId,
  page = 1,
  items = FAVOURITES_PAGE_SIZE,
} = {}) {
  if (!MOSIDA_API_BASE_URL) {
    throw new Error('API base URL is not set. Configure REACT_APP_API_BASE_URL in .env');
  }
  if (!userId) {
    return { items: [], page: 1, itemsPerPage: items, hasMore: false, raw: null };
  }

  const pageNum = Math.max(1, Number(page) || 1);
  const pageSize = Math.max(1, Number(items) || FAVOURITES_PAGE_SIZE);

  const params = new URLSearchParams();
  params.set('userId', String(userId));
  params.set('page', String(pageNum));
  params.set('items', String(pageSize));

  const response = await fetch(`${MOSIDA_API_BASE_URL}/favourites?${params.toString()}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.message || `Favourites failed (${response.status})`);
    error.response = { data: payload, status: response.status };
    throw error;
  }

  const list = extractList(payload)
    .map(normalizeFavourite)
    .filter(Boolean);

  return {
    items: list,
    page: pageNum,
    itemsPerPage: pageSize,
    hasMore: resolveHasMore(payload, pageNum, pageSize, list.length),
    raw: payload,
  };
}
